import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarCheck } from 'lucide-react';
import DOMPurify from 'dompurify';
import { PORTAL_URL } from '../SiteLayout';
import { resolveArticleImage } from '../utils/articleUtils';
import { formatDate } from '../../utils/formatDate';
import PageSEO from '../../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Balises/attributs autorisés pour un article rédigé côté admin
const SANITIZE_OPTS = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h2', 'h3', 'blockquote', 'hr', 'code'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
};

function isHtml(content) {
  return typeof content === 'string' && /<\/?[a-z][\s\S]*>/i.test(content);
}

export default function SiteArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/journal/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('404');
        return r.json();
      })
      .then(setArticle)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="site-section" data-testid="site-article-notfound" style={{ textAlign: 'center' }}>
        <h2>Article introuvable</h2>
        <Link to="/journal" className="site-btn site-btn-outline" style={{ marginTop: '1.5rem' }}>
          <ArrowLeft size={16} /> Retour au journal
        </Link>
      </main>
    );
  }

  if (!article) return <main className="site-section" />;

  const cover = resolveArticleImage(article.image_url);
  const contentIsHtml = isHtml(article.content);

  return (
    <main data-testid="site-article">
      <PageSEO
        title={article.title}
        description={(article.excerpt || (article.content || '').replace(/<[^>]*>/g, '').slice(0, 155)).trim()}
        path={`/journal/${slug}`}
        image={cover || undefined}
        type="article"
        article={{
          title: article.title,
          image: cover,
          published_at: article.created_at,
          updated_at: article.updated_at,
          author: article.author,
        }}
      />
      <div className="site-page-head">
        <div className="site-page-head-inner">
          <span className="site-article-cat" style={{ background: 'rgba(255,255,255,0.18)', color: 'white' }}>
            {article.category}
          </span>
          <h1 className="site-reveal" style={{ marginTop: '0.8rem' }}>{article.title}</h1>
          <p className="site-reveal-1">
            {article.author} · {formatDate(article.created_at)}
          </p>
        </div>
      </div>

      <section className="site-section" style={{ maxWidth: 800 }}>
        <Link to="/journal" style={{ color: 'var(--site-primary)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '1.6rem' }}>
          <ArrowLeft size={16} /> Retour au journal
        </Link>

        {cover && (
          <img
            src={cover}
            alt={article.title}
            data-testid="site-article-cover"
            className="site-article-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}

        {contentIsHtml ? (
          <article
            className="site-article-body site-article-body-html"
            data-testid="site-article-body"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content, SANITIZE_OPTS) }}
          />
        ) : (
          <article className="site-article-body" data-testid="site-article-body">
            {(article.content || '').split('\n\n').map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </article>
        )}

        <div className="site-cta-band" style={{ marginTop: '3rem' }}>
          <div>
            <h2>Une question de santé ?</h2>
            <p>Nos médecins sont disponibles en consultation au centre ou en téléconsultation vidéo.</p>
          </div>
          <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="article-cta-booking-btn">
            <CalendarCheck size={18} /> Prendre rendez-vous
          </a>
        </div>
      </section>
    </main>
  );
}
